import { addTrack } from '../redux/slices/musicPlayerSlice';
import { buildLazyAudioSrc } from './cloudPlaylistTracks';
import { entityId } from './entityId';
import { isSamplesOnly } from './webOnly';

// The source for queueing a catalog track, full-first: a local file plays as
// is; otherwise /lazy-audio (its YouTube/SoundCloud link, or the lazy search
// by id, resolved when the player reaches it, the sample only as its
// fallback); the bare sample only when there is nothing else to try.
// Returns { musicSrc, originalUrl? } or null.
export const fullFirstQueueSrc = (track = {}) => {
    const sample = track.previewUrl || track.sampleUrl || track.preview_url || track.preview || null;
    if (track.filePath) return { musicSrc: track.filePath };
    if (!isSamplesOnly && track.playbackBlocked !== true) {
        const lazy = buildLazyAudioSrc({
            youtubeId: track.youtubeId || track.youtube_id,
            soundcloudUrl: track.soundcloudUrl || track.soundcloud_url,
            trackPublicId: entityId(track),
            artist: track.artist,
            title: track.title,
            duration: track.duration,
            sample,
        });
        if (lazy) return lazy.originalUrl ? lazy : { musicSrc: lazy.musicSrc };
    }
    return sample ? { musicSrc: sample } : null;
};

// Kept for its call sites; the src-uniquifying (`#qN` fragment) that used to
// live here moved into the addTrack reducer itself so EVERY add path is
// covered, not just callers that knew about this helper.
export const addToQueueUnique = (dispatch, entry) => dispatch(addTrack(entry));

// Confirmation card for an explicit user "Add to Next Up" — "<Title> was
// added to Next Up." (see ConfirmationToastBridge). A window event, like the
// like toast, so callers outside React (thunks, plain handlers) can raise it
// and the bridge inside ToastProvider renders it.
export const QUEUE_TOAST_EVENT = 'mg:queue-toast';
export const emitQueueToast = ({ title, artist, artwork } = {}) => {
    if (typeof window === 'undefined') return;
    try {
        window.dispatchEvent(new CustomEvent(QUEUE_TOAST_EVENT, { detail: { title, artist, artwork } }));
    } catch { /* non-DOM env */ }
};

// Explicit "Add to Next Up" from a row/menu: enqueue + confirmation card. Play
// paths (resetAudioList + addTrack) must NOT use this — they aren't "adds".
// The `nextUp` stamp is how the native queue effect tells a deliberate add
// apart from the mechanical audioList appends the advance path makes — a
// deliberate add must splice in right after the playing track even when its
// url is already in the surface, a mechanical append never may (it created
// phantom duplicates next to the playhead when it did).
export const addToNextUp = (dispatch, entry) => {
    dispatch(addTrack({ ...entry, nextUp: true }));
    emitQueueToast({ title: entry?.name, artist: entry?.singer, artwork: entry?.cover || null });
};

// Open the Next Up panel (MusicPlayer listens; setCustomPanelOpen(true)).
export const OPEN_QUEUE_EVENT = 'mg:open-queue';
export const requestOpenQueue = () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(OPEN_QUEUE_EVENT));
};
